/**
 * Writing V4 authorisation rules; utils/v4Permissions.js is the read side.
 *
 * `PUT /v1/permissions/{account}/accounts/{user}` REPLACES the rule set, so
 * every caller must aim at a pair that holds nothing yet — a just-created user
 * or a just-created account.
 */

import API, {v4Unscoped} from '../../api/V4client';
import {buildV4AccountTree} from '../../utils/v4AccountTree';
import {v4PermissionCeiling, v4RulesCover} from '../../utils/v4Permissions';

/** Full access — what V3 gave every root-account user in its customers. */
export const V4_ADMIN_RULES = ['*'];

// A null (unknown) ceiling caps nothing.
export const v4MayGrant = ceiling => rule => !ceiling || v4RulesCover(ceiling, rule);

export const v4GrantCeiling = (currentUser, accountId) =>
    (currentUser?.isV4Native && currentUser.isAccountManager
        ? v4PermissionCeiling(currentUser.get('v4Permissions'), accountId)
        : null);

export const v4GrantCapped = (mayGrant, held, saved) => rule =>
    !held.has(rule) && !(saved || []).includes(rule) && !mayGrant(rule);

/**
 * Unscoped, as a scoped list adds parent and siblings; archived rows keep live children reachable.
 */
export async function fetchV4SubAccounts(accountUuid) {
    const rows = res => (Array.isArray(res?.data) ? res.data : (res?.data?.accounts || []));
    const [active, archived] = await Promise.all([
        API.accountsList(undefined, v4Unscoped()),
        API.accountsList({archived: true}, v4Unscoped()).catch(() => null),
    ]);
    const byId = new Map();
    [...rows(active), ...rows(archived)].forEach(a => {
        const key = String(a?.id || '').lc();
        if (key && !byId.has(key)) byId.set(key, a);
    });
    const tree = buildV4AccountTree([...byId.values()], accountUuid);
    return tree.descendantsOf(accountUuid)
        .filter(node => !node.archived)
        .map(node => ({...node.account, path: tree.pathLabel(node.id)}));
}

// One PUT per grant — no batch endpoint. A fixed pool rather than fixed batches:
// a barrier would idle on each round's slowest request.
const CONCURRENCY = 12;

/**
 * Applies `{accountId, userId, rules}` grants, returning the ones that failed.
 * Never rejects — a sub-account the caller can't administer must not cost the
 * grants that did land. Empty `rules` is reported as a failure rather than sent:
 * `SetPermissionsRequest.rules` is `minItems: 1`.
 */
export async function applyV4Permissions(grants) {
    const failed = [];
    const queue = (grants || []).filter(g => {
        if (g?.accountId && g?.userId && g?.rules?.length) return true;
        failed.push({...g, reason: new Error('Refusing to send an empty rule set')});
        return false;
    });

    let next = 0;
    const worker = async () => {
        while (next < queue.length) {
            const grant = queue[next++];
            try {
                const args = [grant.accountId, grant.userId, {rules: grant.rules}];
                await API.permissionsAccountsUpdate(...(grant.params ? [...args, grant.params] : args));
            } catch (e) {
                failed.push({...grant, reason: e});
            }
        }
    };
    await Promise.all(Array.from({length: Math.min(CONCURRENCY, queue.length)}, worker));
    return failed;
}

/**
 * A copy of the creator's rules in the creation account, plus `rulesFor(id)`, else full access, in
 * each selected sub-account. Empty `ownRules` means the session's own rights are
 * unknown — the copy is skipped rather than guessed at.
 */
export const newUserGrants = ({userId, accountId, ownRules, subAccountIds, rulesFor}) => {
    const own = String(accountId || '').lc();
    const subAccountRules = id => {
        const rules = typeof rulesFor === 'function' ? rulesFor(id) : null;
        return Array.isArray(rules) && rules.length ? [...rules] : V4_ADMIN_RULES;
    };
    return [
        ...(ownRules?.length ? [{accountId, userId, rules: [...ownRules]}] : []),
        // Not the copied set: those rules can name `accountId` itself
        // (`read.accounts.<uuid>`), which means nothing in a sub-account.
        ...(subAccountIds || [])
            .filter(id => String(id || '').lc() !== own)
            .map(id => ({accountId: id, userId, rules: subAccountRules(id)})),
    ];
};
